import { useState } from "react";
import {
  deviceApi,
  DeviceApiError,
  type DeviceInvoice,
  type DeviceOrder,
  type OrderItemIn,
  type PaymentMethod,
} from "../../infrastructure/deviceApi";
import type { CartItem } from "../../domain/types";

interface CheckoutOptions {
  tipMxn?: number;
  discountMxn?: number;
}

export interface ChargeResult {
  method: PaymentMethod;
  order: DeviceOrder;
  invoice: DeviceInvoice | null;
}

/**
 * Flujo de cobro del POS web sobre el rail v2. Dos caminos desde el mismo carrito:
 *
 *   lightning → POST /orders → POST /orders/{id}/invoice → pantalla de QR
 *   cash      → POST /orders → POST /orders/{id}/cash    → confirmación (ya está cobrado)
 *
 * El efectivo no pasa por la pantalla de espera a propósito: no hay nada que esperar, el
 * dinero ya está en el cajón cuando el cajero toca el botón. Es la misma forma que usa la
 * app móvil, y es cobro que el POS web NO tenía: en v1 sólo existía bitcoin, así que un
 * proveedor caído lo dejaba sin poder vender nada.
 *
 * La propina viaja como línea libre (igual que la app); el descuento va como campo de la
 * orden, porque una línea negativa la rechaza el backend y repartirlo entre las líneas
 * mentiría sobre el precio de cada producto en el recibo.
 */
export function useCharge() {
  const [charging, setCharging] = useState<PaymentMethod | null>(null);
  const [error, setError] = useState<string | null>(null);

  const charge = async (
    items: CartItem[],
    method: PaymentMethod,
    options: CheckoutOptions = {}
  ): Promise<ChargeResult | null> => {
    if (items.length === 0 || charging) return null;
    setCharging(method);
    setError(null);
    try {
      const payload: OrderItemIn[] = items.map((i) => ({
        product_id: i.product.id,
        qty: i.quantity,
      }));
      const tip = options.tipMxn ?? 0;
      if (tip > 0) {
        payload.push({ description: "Propina", qty: 1, unit_price_mxn: tip.toFixed(2) });
      }
      const order = await deviceApi.orders.create(
        payload,
        (options.discountMxn ?? 0).toFixed(2)
      );

      if (method === "cash") {
        const cobrada = await deviceApi.orders.cash(order.id);
        return { method, order: cobrada, invoice: null };
      }
      const invoice = await deviceApi.orders.invoice(order.id);
      return { method, order, invoice };
    } catch (e) {
      setError(mensajeDeCobro(e));
      return null;
    } finally {
      setCharging(null);
    }
  };

  return { charge, charging, error, reset: () => setError(null) };
}

/**
 * Traduce el fallo de cobrar a algo que el CAJERO pueda usar.
 *
 * La distinción que importa: un proveedor de pagos caído **no es un error nuestro**, y el
 * comerciante tiene que poder saberlo para no culpar a la app. Ahora además hay salida: si
 * bitcoin no está disponible, todavía puede cobrar en efectivo.
 */
function mensajeDeCobro(e: unknown): string {
  const code = e instanceof DeviceApiError ? e.code : "";
  if (code === "lightning_unavailable") {
    return "El cobro con bitcoin no está disponible: el proveedor de pagos no responde. No es un problema de esta app — podés cobrar en efectivo.";
  }
  if (code === "network_error") {
    return "No se pudo generar el cobro. Revisá la conexión e intentá de nuevo.";
  }
  if (code === "discount_exceeds_subtotal") {
    return "El descuento es mayor que el total. Ajustalo e intentá de nuevo.";
  }
  if (code === "order_already_paid") {
    return "Esa venta ya estaba cobrada.";
  }
  if (code === "empty_order") {
    return "El carrito está vacío.";
  }
  return "No se pudo generar el cobro. Intentá de nuevo.";
}
